import React, { useEffect, useState } from "react";
import { Button, InputNumber, Space, message } from "antd";
import { CheckOutlined, EditOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { setMpReservedRemnantsManage } from "api/RemnantsManage";

type Props = {
  article: string;
  mpWarehouseId: number | null | undefined;
  value: number | null | undefined;
  onRecalculated?: () => void | Promise<void>;
};

/** Test-only editable MP reserve (orders) → local waterfall recompute. */
const ReservedQtyCell: React.FC<Props> = ({
  article,
  mpWarehouseId,
  value,
  onRecalculated,
}) => {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [draft, setDraft] = useState<number>(Number(value) || 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(Number(value) || 0);
  }, [value, editing]);

  const apply = async (next: number) => {
    if (mpWarehouseId == null || mpWarehouseId <= 0) {
      message.warning("Нет склада МП в строке");
      return;
    }
    const qty = Math.max(0, Math.floor(next) || 0);
    if (qty === (Number(value) || 0)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await setMpReservedRemnantsManage({
        article,
        mp_warehouse_id: mpWarehouseId,
        qty,
      });
      setEditing(false);
      message.success("Резерв обновлён, остатки пересчитаны");
      await onRecalculated?.();
    } catch {
      message.error("Не удалось обновить резерв");
    } finally {
      setSaving(false);
    }
  };

  if (mpWarehouseId == null || mpWarehouseId <= 0) {
    return (
      <span>
        {value == null || Number.isNaN(Number(value)) ? "—" : Number(value)}
      </span>
    );
  }

  if (editing) {
    return (
      <Space.Compact>
        <InputNumber
          size="small"
          min={0}
          step={1}
          value={draft}
          autoFocus
          disabled={saving}
          onChange={(v) => setDraft(Math.max(0, Math.floor(Number(v) || 0)))}
          onPressEnter={() => apply(draft)}
          style={{ width: 72 }}
        />
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          loading={saving}
          onClick={() => apply(draft)}
        />
      </Space.Compact>
    );
  }

  const current = Number(value) || 0;

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        minHeight: 22,
      }}
    >
      <span
        onClick={() => setEditing(true)}
        style={{ cursor: "pointer", minWidth: 24 }}
      >
        {current}
      </span>
      {hovered || saving ? (
        <>
          <Button
            size="small"
            type="text"
            icon={<MinusOutlined />}
            disabled={saving || current <= 0}
            onClick={() => apply(current - 1)}
            title="−1 заказ (тест)"
          />
          <Button
            size="small"
            type="text"
            icon={<PlusOutlined />}
            disabled={saving}
            onClick={() => apply(current + 1)}
            title="+1 заказ (тест)"
          />
          <EditOutlined
            style={{ color: "#1677ff", cursor: "pointer", fontSize: 12 }}
            onClick={() => setEditing(true)}
          />
        </>
      ) : null}
    </span>
  );
};

export default ReservedQtyCell;
